import { Button, ButtonMode, ButtonSize, colors, Icon, ICONS, Tooltip } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import React, { ReactElement } from 'react';
import useFetchUserPermissions from '../../../../../hooks/permissions/useFetchUserPermissions';
import { hasPermissionToExport } from '../../../../../utils/permissionsUtils';
import styles from './MonitorActions.module.scss';

const cx = classNames.bind(styles);

interface MonitorActionsProps {
  searchText: string;
  onSearchTextChange: (text: string) => void;
  onManageColumns?: () => void;
  onExport?: () => void;
  isExportDisabled: boolean;
  onExpand?: () => void;
}

const MonitorActions = ({
  searchText,
  onSearchTextChange,
  onManageColumns,
  onExpand,
  onExport,
  isExportDisabled,
}: MonitorActionsProps): ReactElement => {
  const { data: userPermissions } = useFetchUserPermissions();

  const renderSearchInput = () => (
    <div className={cx('search-input-container')}>
      <div className={cx('left-icon')} data-testid="search-icon">
        <Icon name={ICONS.Search} height={12} width={12} />
      </div>
      <input
        className={cx('search-input')}
        value={searchText || ''}
        autoComplete="off"
        aria-label="Search Companies"
        maxLength={200}
        onChange={(event) => onSearchTextChange(event.target.value)}
        placeholder="Search Companies"
        data-testid="search-input"
      />
      {searchText && (
        <Button
          ariaLabel="clear input"
          className={cx('clear-button')}
          dataTestId="search-clear-button"
          onClick={() => onSearchTextChange('')}
        >
          <Icon name={ICONS.Clear} width={12} height={12} />
        </Button>
      )}
    </div>
  );

  return (
    <div className={cx('body')}>
      {renderSearchInput()}
      <Button
        mode={ButtonMode.SECONDARY}
        size={ButtonSize.SMALL}
        onClick={onManageColumns}
        dataTestId="manage-columns-button"
      >
        Columns
        <Icon name={ICONS.Columns} width={12} height={12} color={colors['white-color']} />
      </Button>
      {onExpand && (
        <Button mode={ButtonMode.SECONDARY} size={ButtonSize.SMALL} onClick={onExpand} dataTestId="expand-table-button">
          <Icon name={ICONS.Expand} width={12} height={12} />
          Expand
        </Button>
      )}
      {!isExportDisabled && (
        <Tooltip
          trigger={
            <span>
              <Button
                mode={ButtonMode.SECONDARY}
                size={ButtonSize.SMALL}
                onClick={onExport}
                dataTestId="export-table-button"
              >
                <Icon name={ICONS.Download} width={12} height={12} />
                Export
              </Button>
            </span>
          }
          content="Upgrade Your Subscription to Export"
          disabled={hasPermissionToExport(userPermissions)}
        />
      )}
    </div>
  );
};

export default MonitorActions;
